import AppShell from "../../components/app/AppShell";
import { Eyebrow } from "../../components/design";
import { STUB_POSTAL_CARD } from "../../lib/stubs";

// /app/postal-card — DHL-style tracking timeline + handwritten card mock.
// Reference: extras.jsx PagePostalStatus.

const STATE_LABELS: Record<string, string> = {
  ordered: "Bestellung aufgegeben",
  written: "Karte geschrieben",
  shipped: "Bei DHL eingeliefert",
  transit: "In Zustellung",
  delivered: "Zugestellt",
};

function formatDateTime(iso: string | null) {
  if (!iso) return "voraussichtlich";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", {
      weekday: "long",
      day: "2-digit",
      month: "long",
    });
  } catch {
    return iso;
  }
}

export default function AppPostalCard() {
  const card = STUB_POSTAL_CARD;
  return (
    <AppShell
      eyebrow="Karte für Hildegard"
      title={
        <>
          Die Karte <em>ist unterwegs</em>.
        </>
      }
      backTo="/app"
    >
      <div style={{ padding: "0 22px 90px" }}>
        {/* Postcard mock — A6 proportions, slight rotation, tape strip top-left */}
        <div
          style={{
            background: "var(--paper-50)",
            borderRadius: 6,
            padding: "20px 22px",
            border: "1px solid rgba(43,26,15,0.10)",
            boxShadow: "0 14px 28px -10px rgba(43,26,15,0.20)",
            transform: "rotate(-1.2deg)",
            margin: "0 0 28px",
            position: "relative",
            backgroundImage:
              "radial-gradient(ellipse at 20% 10%, rgba(168,122,58,0.08), transparent 55%)",
            aspectRatio: "1.41 / 1", // A6 landscape proportion
          }}
        >
          {/* Tape strip top-left */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: -6,
              left: 16,
              width: 56,
              height: 16,
              background: "rgba(168,122,58,0.55)",
              boxShadow: "0 1px 2px rgba(43,26,15,0.10)",
              transform: "rotate(-3deg)",
            }}
          />
          {/* Stamp area */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              top: 12,
              right: 14,
              width: 38,
              height: 46,
              border: "1px dashed rgba(43,26,15,0.30)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--f-ui)",
              fontSize: 8,
              color: "var(--ink-faint)",
              textAlign: "center",
              lineHeight: 1.2,
            }}
          >
            Briefmarke 70 ct
          </div>

          <div
            style={{
              fontFamily: "var(--f-ui)",
              fontSize: 9,
              letterSpacing: "0.20em",
              color: "var(--ochre)",
              textTransform: "uppercase",
              fontWeight: 700,
              marginBottom: 12,
            }}
          >
            Vitanara · {card.phone_on_card}
          </div>

          <div
            className="t-script"
            style={{
              fontSize: 22,
              color: "var(--oxblood)",
              lineHeight: 1.3,
              marginBottom: 10,
              transform: "rotate(-1deg)",
            }}
          >
            Liebe {card.recipient.split(" ")[0]},
          </div>
          <p
            style={{
              fontFamily: "var(--f-script)",
              fontSize: 17,
              lineHeight: 1.45,
              color: "var(--ink)",
              margin: "0 0 10px",
            }}
          >
            Ihre Enkelin Marie hat etwas Schönes geplant. Ich heiße Konrad und
            rufe Sie am Sonntag, den 24. Mai um 10:30 Uhr an. Wir haben Zeit. —
            Bis dann!
          </p>
          <div
            style={{
              fontFamily: "var(--f-script)",
              fontSize: 18,
              color: "var(--oxblood)",
              textAlign: "right",
              transform: "rotate(-2deg)",
            }}
          >
            Konrad ✦
          </div>
        </div>

        {/* Tracking */}
        <Eyebrow>Status · DHL Standardbrief</Eyebrow>
        <div
          style={{
            background: "var(--paper-50)",
            border: "1px solid rgba(43,26,15,0.10)",
            borderRadius: 14,
            padding: "8px 18px",
            marginBottom: 18,
          }}
        >
          {card.timeline.map((it, i, arr) => {
            const last = i === arr.length - 1;
            const isDone = it.state === "done";
            const isActive = it.state === "active";
            const label = it.label || STATE_LABELS[it.key] || it.key;
            return (
              <div
                key={it.key}
                style={{
                  display: "flex",
                  gap: 14,
                  padding: "14px 0",
                  borderBottom: !last
                    ? "1px dashed rgba(43,26,15,0.12)"
                    : "none",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: 14,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: isDone
                        ? "var(--sage)"
                        : isActive
                          ? "var(--oxblood)"
                          : "var(--paper-200)",
                      border: !isDone && !isActive
                        ? "1.5px solid rgba(43,26,15,0.20)"
                        : "none",
                      marginTop: 4,
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      animation: isActive
                        ? "vt-pulse-fill 1.6s ease-in-out infinite"
                        : undefined,
                    }}
                  >
                    {isDone && (
                      <svg
                        width="8"
                        height="8"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--paper-50)"
                        strokeWidth="4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {isActive && (
                      <div
                        aria-hidden
                        style={{
                          position: "absolute",
                          inset: -4,
                          borderRadius: "50%",
                          border: "1.5px solid rgba(107,32,24,0.30)",
                          animation: "vt-pulse-ring 1.6s ease-out infinite",
                        }}
                      />
                    )}
                  </div>
                  {!last && (
                    <div
                      style={{
                        flex: 1,
                        width: 1,
                        background: "rgba(43,26,15,0.12)",
                        marginTop: 4,
                      }}
                    />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontFamily: "var(--f-display)",
                      fontWeight: 500,
                      fontSize: 14,
                      color:
                        isDone || isActive ? "var(--ink)" : "var(--ink-faint)",
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--f-body)",
                      fontStyle: "italic",
                      fontSize: 12,
                      color: "var(--ink-faint)",
                    }}
                  >
                    {formatDateTime(it.at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--ink-soft)",
            textAlign: "center",
            marginBottom: 18,
          }}
        >
          Voraussichtliche Zustellung: {formatDate(card.expected_at)}
        </div>
      </div>
    </AppShell>
  );
}
